import AutoOrderedIcon from '@assets/icons/auto-ordered.svg';
import CheckmarkIcon from '@assets/icons/checkmark.svg';
import ExclamationPoint from '@assets/icons/exclamation-point.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import ReviewArrowIcon from '@assets/icons/review-arrow-icon.svg';
import RightArrowIcon from '@assets/icons/arrow-right-small.svg';
import { CheckInLabel } from '@/components/check-in-label';
import { createStyles, Theme } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import {
  FulfillmentName,
  OrderInvoiceCheckInFieldsFragment,
  OrderSource,
  OrderStatusLabelFieldsFragment,
} from '@/types';
import { gql } from '@apollo/client';
import { OrderStatus } from '@/enums';
import { StyleProp, Text, TextStyle, View } from 'react-native';
import { SvgProps } from 'react-native-svg';

export const ORDER_STATUS_LABEL_FIELDS = gql`
  fragment OrderStatusLabelFields on orders {
    order_items_with_exception: orders_items_aggregate(
      where: { order_item_rating: { rating: { _lt: 5 } } }
    ) {
      aggregate {
        count
      }
    }
    created_source
    fulfillment_type
    status
    uuid
  }
`;

interface OrderStatusOptions {
  Icon: React.FC<SvgProps>;
  iconColor: string;
  label: string;
}

const getOrderOptions = (status: OrderStatus, theme: Theme) => {
  const orderStatusOptionsMap: {
    [status in OrderStatus]?: OrderStatusOptions;
  } = {
    [OrderStatus.AutoOrdered]: {
      Icon: AutoOrderedIcon,
      iconColor: theme.sk.color.text.blue,
      label: i18n.t('auto_ordered'),
    },
    [OrderStatus.Cancelled]: {
      Icon: ExclamationPoint,
      iconColor: theme.colors.caution,
      label: i18n.t('cancelled'),
    },
    [OrderStatus.InExternalReview]: {
      Icon: ReviewArrowIcon,
      iconColor: theme.colors.warningContent,
      label: i18n.t('in_review'),
    },
    [OrderStatus.ReceivedBySupplier]: {
      Icon: RightArrowIcon,
      iconColor: theme.colors.primary,
      label: i18n.t('order_received'),
    },
    [OrderStatus.SentToSupplier]: {
      Icon: RightArrowIcon,
      iconColor: theme.colors.blue,
      label: i18n.t('order_sent'),
    },
    [OrderStatus.ViewedBySupplier]: {
      Icon: RightArrowIcon,
      iconColor: theme.colors.blue,
      label: i18n.t('order_sent'),
    },
  };

  return orderStatusOptionsMap[status];
};

const FULFILLMENT_TYPE_TO_ORDER_STATUS_OPTION_OVERRIDES_MAP: {
  [fulfillmentType in FulfillmentName]?: {
    [status in OrderStatus]?: Partial<OrderStatusOptions>;
  };
} = {
  [FulfillmentName.Pickup]: {
    [OrderStatus.Delivered]: {
      label: i18n.t('order_picked_up'),
    },
    [OrderStatus.OutForDelivery]: {
      label: i18n.t('pickup_today'),
    },
    [OrderStatus.ShouldBeDelivered]: {
      label: i18n.t('pickup_today'),
    },
  },
};

const isSubscriptionOrder = (order: OrderStatusLabelFieldsFragment) =>
  order.created_source === OrderSource.Subscription;

const getOrderStatusOptions = (
  order: OrderStatusLabelFieldsFragment,
  theme: Theme,
): OrderStatusOptions | null => {
  const status = order.status as OrderStatus;
  const orderOptions = isSubscriptionOrder(order)
    ? getOrderOptions(OrderStatus.AutoOrdered, theme)
    : getOrderOptions(status, theme);
  if (!orderOptions) {
    return null;
  }

  const fulfillmentType = order.fulfillment_type as FulfillmentName;
  const options = {
    ...orderOptions,
    ...(FULFILLMENT_TYPE_TO_ORDER_STATUS_OPTION_OVERRIDES_MAP[
      fulfillmentType
    ]?.[status] || {}),
  };

  if (
    order.status === OrderStatus.Rated &&
    (order.order_items_with_exception.aggregate?.count ?? 0) > 0
  ) {
    return {
      ...options,
      Icon: ExclamationPoint,
      iconColor: theme.colors.caution,
    };
  }

  return options;
};

interface Props {
  additionalText?: React.ReactNode;
  existingCheckIn?: OrderInvoiceCheckInFieldsFragment | null;
  labelStyle?: StyleProp<TextStyle>;
  order: OrderStatusLabelFieldsFragment;
  showCheckInStatusLabel?: boolean | null;
}

export const OrderStatusLabel: React.FC<Props> = ({
  additionalText,
  existingCheckIn,
  labelStyle,
  order,
  showCheckInStatusLabel,
}) => {
  const [styles, theme] = useStyles();

  if (showCheckInStatusLabel) {
    return (
      <CheckInLabel existingCheckIn={existingCheckIn} labelStyle={labelStyle} />
    );
  }

  const status = order.status as OrderStatus;
  if (status === OrderStatus.ConfirmedBySupplier) {
    return (
      <View style={styles.base}>
        <View style={[styles.icon, { backgroundColor: theme.colors.primary }]}>
          <CheckmarkIcon
            height={12}
            stroke={theme.sk.color.icon.ondark}
            testID="order-status-icon"
            width={12}
          />
        </View>
        <Text style={[styles.label, labelStyle]}>
          {i18n.t('order_confirmed')}
        </Text>
      </View>
    );
  }

  const options = getOrderStatusOptions(order, theme);
  if (options) {
    const { Icon, iconColor, label } = options;
    return (
      <View style={styles.base}>
        <View style={[styles.icon, { backgroundColor: iconColor }]}>
          <Icon
            fill={
              isSubscriptionOrder(order)
                ? theme.colors.transparent
                : theme.colors.white
            }
            testID="order-status-icon"
          />
        </View>
        <Text style={[styles.label, labelStyle]}>{label}</Text>
      </View>
    );
  }
  return additionalText ? (
    <View style={styles.base}>
      <Text style={[styles.label, labelStyle]}>{additionalText}</Text>
    </View>
  ) : (
    // Always renders View to keep invoice info right aligned
    <View />
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  icon: {
    alignItems: 'center',
    borderRadius: 16,
    height: 16,
    justifyContent: 'center',
    marginRight: 4,
    width: 16,
  },
  label: {
    ...FONTS.primaryBold,
    color: theme.colors.inactiveText,
    fontSize: theme.fontSizes.xsmall,
    textTransform: 'uppercase',
  },
}));
